import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, type SupportTicket, type TicketMessage } from "@clevis/shared-types";
import { Button, Card, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";

export function TicketThread({ ticketId, ticket, onBack }: { ticketId: string; ticket: SupportTicket; onBack: () => void }) {
  const { superAdmin, impersonation } = useAuth();
  const asSupport = superAdmin && !impersonation;
  const [messages, setMessages] = useState<Array<TicketMessage & { id: string }>>([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = query(collection(db, paths.supportTicketMessages(ticketId)), orderBy("createdAt"));
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TicketMessage) })));
    });
  }, [ticketId]);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await call("replyToSupportTicket", { ticketId, body });
      setBody("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: SupportTicket["status"]) {
    setError("");
    try {
      await call("updateSupportTicket", { ticketId, status });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
            ← Tickets
          </button>
          <h2 className="text-base font-bold text-ink">{ticket.subject}</h2>
          <p className="text-xs text-steel">
            {ticket.category} · {ticket.priority} · {ticket.status.replaceAll("_", " ")}
          </p>
        </div>
        {ticket.status !== "closed" ? (
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void setStatus("closed")}>
            Close ticket
          </Button>
        ) : (
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void setStatus("open")}>
            Reopen
          </Button>
        )}
      </div>

      <Card className="space-y-3">
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "max-w-[85%] rounded-xl px-3 py-2 text-sm",
              m.voice === "support" ? "bg-ink text-white" : "ml-auto bg-cream text-ink",
            )}
          >
            <div className="mb-0.5 text-[10px] uppercase tracking-wide opacity-60">
              {m.voice === "support" ? "Clevis Support" : "Customer"} ·{" "}
              {new Date(m.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
            <div className="whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
        {messages.length === 0 ? <p className="text-center text-sm text-steel">No messages yet.</p> : null}
      </Card>

      <form onSubmit={send} className="flex gap-2">
        <textarea
          className="min-h-[60px] flex-1 rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink focus:border-brandorange focus:outline-none"
          placeholder={asSupport ? "Reply as Clevis Support…" : "Write a reply…"}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
        />
        <Button type="submit" busy={busy} className="self-end">
          Send
        </Button>
      </form>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
